import React from 'react';
import { ArrowUpDown } from 'lucide-react';
import { useLanguage } from '../../../features/language/useLanguage';
import { formatDateForGroup } from '../../../utils/date';
import { Pagination } from '../../../components/common/Pagination';
import type { SalesOrder } from '../../../types/salesOrder';
import { SalesOrderTableRow } from './SalesOrderTableRow';
import type { SalesOrderRowActions } from './types';

interface SortableHeaderProps {
  column: keyof SalesOrder;
  label: string;
  sortKey?: keyof SalesOrder | null;
  onSort: (key: keyof SalesOrder) => void;
}

/** A clickable, sortable column header that shows an indicator when active. */
function SortableHeader({ column, label, sortKey, onSort }: SortableHeaderProps) {
  return (
    <th
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
      onClick={() => onSort(column)}
    >
      <div className="flex items-center gap-2">
        {label}
        {sortKey === column && <ArrowUpDown className="h-4 w-4" />}
      </div>
    </th>
  );
}

interface SalesOrderTableProps extends SalesOrderRowActions {
  orders: SalesOrder[];
  isEmpty: boolean;
  grouped: boolean;
  groupedOrders: Record<string, SalesOrder[]>;
  sortedDates: string[];
  sortKey?: keyof SalesOrder | null;
  onSort: (key: keyof SalesOrder) => void;
  page: number;
  size: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  loading: boolean;
}

/** Desktop table of sales orders with sortable headers, optional date grouping and pagination. */
export function SalesOrderTable({
  orders,
  isEmpty,
  grouped,
  groupedOrders,
  sortedDates,
  sortKey,
  onSort,
  page,
  size,
  total,
  onPageChange,
  onPageSizeChange,
  loading,
  ...actions
}: SalesOrderTableProps) {
  const { t } = useLanguage();

  return (
    <div className="hidden md:block bg-white shadow overflow-hidden rounded-lg overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <SortableHeader column="orderNumber" label={t('salesOrder.list.table.orderNumber')} sortKey={sortKey} onSort={onSort} />
            <SortableHeader column="customer" label={t('salesOrder.list.table.customer')} sortKey={sortKey} onSort={onSort} />
            <SortableHeader column="orderDate" label={t('salesOrder.list.table.date')} sortKey={sortKey} onSort={onSort} />
            <SortableHeader column="totalAmount" label={t('salesOrder.list.table.total')} sortKey={sortKey} onSort={onSort} />
            <SortableHeader column="status" label={t('salesOrder.list.table.deliveryStatus')} sortKey={sortKey} onSort={onSort} />
            <SortableHeader column="paymentStatus" label={t('salesOrder.list.table.paymentStatus')} sortKey={sortKey} onSort={onSort} />
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('salesOrder.list.table.internalNotes')}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {isEmpty ? (
            <tr>
              <td colSpan={7} className="italic px-6 py-4 text-center text-sm text-gray-500">
                {t('salesOrder.list.empty')}
              </td>
            </tr>
          ) : grouped ? (
            sortedDates.map((dateKey) => (
              <React.Fragment key={dateKey}>
                <tr className="bg-gray-100">
                  <td colSpan={7} className="px-6 py-2 text-sm font-semibold text-gray-700">
                    {formatDateForGroup(dateKey)}
                  </td>
                </tr>
                {groupedOrders[dateKey].map((so) => (
                  <SalesOrderTableRow key={so.id} so={so} {...actions} />
                ))}
              </React.Fragment>
            ))
          ) : (
            orders.map((so) => <SalesOrderTableRow key={so.id} so={so} {...actions} />)
          )}
        </tbody>
      </table>
      <Pagination
        page={page}
        pageSize={size}
        total={total}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        isLoading={loading}
      />
    </div>
  );
}
